import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { acceptInvite, getInvite } from '@/entities/user';
import { useAuth } from '@/features/auth';
import { ApiError } from '@/shared/api';
import { labelOf, useI18n } from '@/shared/i18n';
import { Logo } from '@/shared/ui/logo';
import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/shadcn/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/form';
import { Input } from '@/shared/ui/shadcn/input';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';

type FormValues = { email: string; password: string; confirm_password: string };

export function InvitePage() {
  const { t } = useI18n();
  const schema = useMemo(
    () =>
      z
        .object({
          email: z.string().email(t.common.invalidEmail),
          password: z.string().min(8, t.invite.passwordMin),
          confirm_password: z.string().min(1, t.invite.confirmPassword),
        })
        .refine((values) => values.password === values.confirm_password, {
          message: t.invite.mismatch,
          path: ['confirm_password'],
        }),
    [t],
  );
  const { token } = useParams<{ token: string }>();
  const { applySession, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const inviteQuery = useQuery({
    queryKey: ['invite', token],
    queryFn: () => getInvite(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', confirm_password: '' },
  });

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!authLoading && isAuthenticated) {
    return <Navigate to="/playground" replace />;
  }

  async function onSubmit(values: FormValues) {
    setError(null);
    try {
      const result = await acceptInvite(token!, values.email, values.password);
      applySession(result);
      navigate('/playground', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.invite.failed);
    }
  }

  const inviteError =
    inviteQuery.error instanceof ApiError
      ? inviteQuery.error.message
      : inviteQuery.isError
        ? t.invite.notFound
        : null;

  return (
    <div className="flex min-h-svh items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Logo variant="mark" className="size-8" />
            <span className="text-lg font-semibold">{t.app.name}</span>
          </div>
          <div>
            <CardTitle>{t.invite.title}</CardTitle>
            <CardDescription>
              {inviteQuery.isSuccess
                ? t.invite.asRole(labelOf(t.roles, inviteQuery.data.role_key))
                : t.invite.fallback}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {inviteQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : inviteError ? (
            <Alert variant="destructive">
              <AlertDescription>{inviteError}</AlertDescription>
            </Alert>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {error ? (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : null}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.common.email}</FormLabel>
                      <FormControl>
                        <Input type="email" autoComplete="username" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.common.password}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirm_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.invite.confirmPassword}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? t.invite.submitting : t.invite.submit}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
